import { useQuery } from "@tanstack/react-query";

import { getMarketFreshness, metaFrom, type FreshnessMeta } from "./api";
import { ExplorerTab } from "./ExplorerTab";
import { SetTab } from "./SetTab";

/**
 * The freshness bridge (native-module contract step 2).
 *
 * Called UNCONDITIONALLY from `useNativeModuleMeta`, gated internally by
 * `enabled`, because that function runs on every render for every module and a
 * conditional hook call there breaks the rules of hooks for all of them.
 *
 * The market-freshness read is the source rather than a tab's own payload: it
 * is the one call that covers every marketplace, so the line the shell draws
 * reflects the WORST collection rather than whichever marketplace happens to be
 * selected. It shares the key `SetTab` uses, so the two are one cache entry.
 */
export function useBenchmarkMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const head = useQuery({
    queryKey: ["meteoric_competitor_benchmark", "market-freshness"],
    queryFn: getMarketFreshness,
    enabled,
  });
  if (!enabled) return undefined;
  return head.data ? metaFrom(head.data) : undefined;
}

/**
 * Competitor Benchmark, rendered natively in the portal.
 *
 * `decision` and `ownerName` are deliberately unread: permission rendering is
 * shell-owned (I-7), and `ShellPage` has already wrapped everything this returns
 * in `PermissionGate` — which makes the Set tab's controls genuinely inert under
 * a `view` decision and states why, once.
 *
 * An unrecognised `activeTabKey` falls back to the Competitive Set: the shell
 * owns the tab bar, so an unknown key means manifest and code have drifted, and
 * a blank viewport hides that while the set tab shows something true.
 */
export function BenchmarkModule({
  activeTabKey,
  areaName,
}: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  if (activeTabKey === "pricing") return <ExplorerTab metric="price" areaName={areaName} />;
  if (activeTabKey === "bsr") return <ExplorerTab metric="bsr" areaName={areaName} />;
  return <SetTab areaName={areaName} />;
}
